import { ensureArray } from 'ramda-adjunct';
import { SingleOrArray, IKeyedObject } from '@dirootie-extensions/typescript';
import { flatten, identity, map, length, propEq, has, filter, T, prop, keys, propOr, concat, union, reduce, assoc } from "ramda";
import { ConfigElement } from "../_types";
import { flatAndMergeAndSetSections, isSection } from "./section";
import { createPipeline } from "./pipeline";
import { ScreenProps } from '@components-common/types';
import { isScreenProps } from './validators';
import { isTemplate, partitionBy } from '../formats/predicates';

const pipeline = createPipeline();

const mapper = {
  "template": pipeline.transform,
  "screen": identity,
} as any;

const partitionByFormat = (elements: ConfigElement[]) => {
  const result = partitionBy([
    {filter:isSection, prop:"section"},
    {filter:isTemplate, prop:"template"},
    {filter:isScreenProps, prop:"screen"},
    {filter:T, prop:"unknown"},
  ])(elements);
  return result;
}

const applyIfPropMatch = <TIn, TOut>(target: IKeyedObject<TIn[]>, funcs: IKeyedObject<(el: TIn) => TOut>, key: string): TOut[] => {
  const func = prop(key, funcs);
  const values = prop(key, target);
  return func ? map(func, values) : [];
};

const mergeArrayProp = (obj1: Record<string, any[]>, obj2: Record<string, any[]>, key: string): any[] => {
  const arr1 = propOr([], key, obj1) as any[];
  const arr2 = propOr([], key, obj2) as any [];
  return concat(arr1, arr2);
};

const commonProps = (obj1: Record<string, any[]>, obj2: Record<string, any[]>) => union(keys(obj1), keys(obj2));


const mergeCommonProps = (obj1: Record<string, any[]>, obj2: Record<string, any[]>): Record<string, any[]> => {
  // Get all unique keys from both objects
  const commonKeys = commonProps(obj1, obj2);

  // Create a merged object by merging arrays for each key
  const mergedObject = reduce(
    (acc, key) => assoc(key, mergeArrayProp(obj1,obj2,key), acc),
    {},
    commonKeys
  );

  return mergedObject;
};

const applyTemplates = <TIn, TOut>(target: IKeyedObject<TIn[]>, funcs: IKeyedObject<(el: TIn) => TOut>): TOut[] => {


  const objKeys = keys(target) as string[];
  const updatedValues = map((key) => applyIfPropMatch(target,funcs,key), objKeys);
  const result = flatten(updatedValues);
  return result as any;
};


const readConfig = (configElements: ConfigElement[]) => {
  const result = partitionByFormat(configElements);
  const sectionElements:any = result.section?.length > 0 ? map(flatAndMergeAndSetSections,result.section) : [];
  const newPartition = partitionByFormat(flatten(sectionElements));
  const resultByType = mergeCommonProps(newPartition, result);
  const screens = applyTemplates<any,ScreenProps>(resultByType, mapper);

  return screens;
}

export type ReadSet = "all" | "test";

const predicateMapper = {
  all: (x:ConfigElement) => true,
  test: (x:any) => has("tag",x) && propEq("test","tag")(x as any)
}

export const createConfigurationReader = (setFilter:ReadSet = "all") => {
  let configElements:ConfigElement[] = [];
  let screens: ScreenProps[] = [];

  const addConfig = (configs: SingleOrArray<ConfigElement>) => {
    const elements = filter(predicateMapper[setFilter],ensureArray(configs));
    configElements = [...configElements, ...elements];
  }

  const getScreens = () => {
    if(length(configElements) === 0) {
      return [];
    }
    if(length(configElements) > 0 && length(screens) === 0) {

      screens = readConfig(configElements);
    }
    return screens;
  }

  return {
    add: addConfig,
    read: () => readConfig(configElements),
    get: getScreens
  }
}
